/**
 * @file api-site-page-publish.ts
 * @description Клиент черновика и версий страниц и мест (phase19.16 Р3а,
 * v0.19.61; SB126–SB128, SB29; контракт docs/api/site-pages.md).
 *
 * Зеркало `apps/api/src/site-pages/admin-site-pages.controller.ts` (маршруты
 * Р3а) и DTO `site-page-publish.service.ts`, `site-page-history.service.ts`.
 * Публикация, «Отменить правки», возврат версии и «Сделать главной» с
 * черновиком шлют отпечаток черновика (`fingerprint`) из `changes(id)`:
 * сервер публикует ровно то, что видел человек, иначе 409
 * `site_page_draft_changed` (разбор — `lib/site-builder/page-publish-errors`).
 *
 * Ломающее изменение Р3а: `POST pages/:id/publish` без тела раньше означал
 * «показать страницу», теперь — публикация с `{ fingerprint }`; «скрыть» —
 * `unpublish`, «показать» — публикация с `show: true`.
 */

import type { SitePageChanges, SitePageDiff, SitePageSnapshot } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { SiteRedirectChange, SiteRedirectTargetInput } from "./api-site-redirects";
import type { SitePageDetail, SitePageKind, SitePageListItem } from "./api-site-pages";

const BASE = "/admin/site/pages";

// ─── DTO ─────────────────────────────────────────────────────────────────

/** Кто правил черновик (`SitePageEditorDto`). */
export interface SitePageEditor {
  id: string;
  name: string;
}

/** Живая версия страницы (`SitePageLiveDto`). */
export interface SitePageLive {
  version: number;
  publishedAt: string;
  publishedByName: string | null;
}

/** Черновик страницы (`SitePageDraftDto`). */
export interface SitePageDraftState {
  /** Счётчик правок черновика. */
  version: number;
  /** Счётчик, при котором черновик совпал с сайтом. */
  syncedVersion: number;
  editedBy: SitePageEditor | null;
  editedAt: string | null;
  /** Последняя правка чужая и не старше 10 минут (`SITE_PAGE_EDITING_WINDOW_MS`). */
  editedByOther: boolean;
}

/** Ошибка проверки перед публикацией (SB127: стоп-лист, `SitePageCheck`). */
export interface SitePageCheck {
  code: string;
  level: "error";
  message: string;
  pageId: string;
  /** Ключ блока (= id строки блока) — кабинет ведёт к нему «Исправить». */
  sectionKey: string;
  path: string;
  term: string;
}

/** «Что изменено» страницы или места (`GET pages/:id/changes`, `SitePageChangesDto`). */
export interface SitePageChangesDto {
  pageId: string;
  title: string;
  kind: SitePageKind;
  slot: string | null;
  slug: string;
  liveSlug: string | null;
  isPublished: boolean;
  fingerprint: string;
  /** Точный признак: черновик отличается от версии на сайте (или её нет). */
  hasChanges: boolean;
  live: SitePageLive | null;
  draft: SitePageDraftState;
  diff: SitePageDiff;
  checks: SitePageCheck[];
}

/** Строка пачки публикации. */
export interface PublishPageItemInput {
  pageId: string;
  fingerprint: string;
  /** Показать скрытую страницу вместе с публикацией. */
  show?: boolean;
  /** Р3в (SB131): переадресовать со старого адреса на сайте, если он сменится; нет — да. */
  redirectFromOldSlug?: boolean;
}

/** Итог по одной странице пачки (`PublishedPageDto`). */
export interface PublishedPage {
  pageId: string;
  /** Номер новой версии; `null` — только показ, версия не создавалась. */
  version: number | null;
  publishedAt: string | null;
  liveSlug: string | null;
  liveSlugChanged: boolean;
  previousLiveSlug: string | null;
  shown: boolean;
  /** Р3в: что сделали переадресации (создана со старого адреса, снята — адрес снова занят). */
  redirect?: SiteRedirectChange | null;
}

export interface PublishPagesResult {
  pages: PublishedPage[];
  /** SB125: ошибки, пропущенные по галочке; без неё — пусто. */
  acknowledged: SitePageCheck[];
}

/** Вид версии (`SiteRevisionKind`). */
export type SitePageRevisionKind = "PUBLISH" | "ROLLBACK" | "IMPORT" | "RESTORE";

/** Строка истории версий (`SitePageRevisionListItemDto`). */
export interface SitePageRevisionItem {
  version: number;
  kind: SitePageRevisionKind;
  sourceVersion: number | null;
  note: string | null;
  slug: string;
  title: string;
  publishedAt: string;
  publishedByName: string | null;
  changes: SitePageChanges;
  /** Сводка словами (SB126): «блоков изменено 2, добавлено 1». */
  summary: string;
  isLive: boolean;
}

/** Одна версия со снимком (`SitePageRevisionDetailDto`): структура при «Посмотреть». */
export interface SitePageRevisionDetail extends SitePageRevisionItem {
  snapshot: SitePageSnapshot;
  replaced: Array<{ path: string; message: string }>;
}

export interface SitePageRevisionList {
  items: SitePageRevisionItem[];
  total: number;
  liveVersion: number | null;
}

/** Предпросмотр «Вернуть версию N» (`SitePageRollbackPreviewDto`). */
export interface SitePageRollbackPreview {
  version: number;
  /** Пропадут неопубликованные правки (без `keepDraft`). */
  lostDraft: { count: number; editedBy: SitePageEditor | null; editedAt: string | null } | null;
  missingMedia: string[];
  designChanged: boolean;
  /** Адрес версии занят — останется нынешний адрес на сайте. */
  slugConflict: { slug: string; takenByPageId: string; willUse: string } | null;
  checks: SitePageCheck[];
  isLive: boolean;
  canRollback: boolean;
}

export interface SitePageRollbackResult {
  version: number;
  sourceVersion: number;
  publishedAt: string;
  liveSlug: string;
  keepDraft: boolean;
  /** Дизайн версии удалён — черновик остался «как у сайта». */
  designDropped: boolean;
}

/** Тело «Сделать главной» (Р2 SB29 + Р3а §1.3). */
export interface SetHomeInput {
  /** Новый адрес прежней главной (обязателен, если она есть). */
  previousSlug?: string;
  /** Скрыть прежнюю главную; по умолчанию нет — остаётся видимой (решение 2026-10-23). */
  hidePrevious?: boolean;
  /** Показать выбранную страницу, если она скрыта. */
  publish?: boolean;
  /** Главной становится черновик (со всеми правками), а не версия на сайте. */
  withDraft?: boolean;
  /** Отпечаток черновика — при `withDraft` и у страницы без версии на сайте. */
  fingerprint?: string;
}

export interface SetHomeResult {
  home: SitePageDetail;
  previous: SitePageListItem | null;
  /** Р3в (SB29, SB131): прежний адрес новой главной → «/». */
  redirect?: SiteRedirectChange | null;
}

// ─── Клиент ──────────────────────────────────────────────────────────────

export const sitePagePublish = {
  /** Страницы и места с правками, «ещё не была на сайте», скрытые с версией — для окна публикации. */
  changesList: () => apiFetch<SitePageChangesDto[]>(`${BASE}/changes`),
  /** «Что изменено» одной страницы, отпечаток и проверки. */
  changes: (pageId: string) => apiFetch<SitePageChangesDto>(`${BASE}/${pageId}/changes`),
  /** Пачка: одна транзакция, один устаревший отпечаток — не публикуется ничего. */
  publishMany: (items: PublishPageItemInput[], options: { acknowledgeChecks?: boolean } = {}) =>
    apiFetch<PublishPagesResult>(`${BASE}/publish`, {
      method: "POST",
      json: { items, ...(options.acknowledgeChecks ? { acknowledgeChecks: true } : {}) },
    }),
  /** Одна страница без окна (`{ fingerprint, show?, acknowledgeChecks? }`). */
  publish: (pageId: string, input: { fingerprint: string; show?: boolean; acknowledgeChecks?: boolean; redirectFromOldSlug?: boolean }) =>
    apiFetch<PublishPagesResult>(`${BASE}/${pageId}/publish`, { method: "POST", json: input }),
  /** Скрыть страницу с сайта (версии сохраняются). */
  unpublish: (pageId: string) => apiFetch<SitePageDetail>(`${BASE}/${pageId}/unpublish`, { method: "POST" }),
  /** «Отменить правки»: черновик := версия на сайте. */
  discard: (pageId: string, fingerprint: string) =>
    apiFetch<SitePageChangesDto>(`${BASE}/${pageId}/discard`, { method: "POST", json: { fingerprint } }),
  /** История версий, новые сверху; `before` — номер, с которого продолжить. */
  revisions: (pageId: string, query: { before?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (query.before !== undefined) q.set("before", String(query.before));
    if (query.limit !== undefined) q.set("limit", String(query.limit));
    const qs = q.toString();
    return apiFetch<SitePageRevisionList>(`${BASE}/${pageId}/revisions${qs ? `?${qs}` : ""}`);
  },
  /** Одна версия со снимком (структура страницы при просмотре версии). */
  revision: (pageId: string, version: number) => apiFetch<SitePageRevisionDetail>(`${BASE}/${pageId}/revisions/${version}`),
  rollbackPreview: (pageId: string, version: number) =>
    apiFetch<SitePageRollbackPreview>(`${BASE}/${pageId}/revisions/${version}/rollback-preview`),
  /** «Вернуть версию N» — новая публикация; `keepDraft` — черновик не трогать («Вернуть как было»). */
  rollback: (pageId: string, version: number, input: { fingerprint: string; keepDraft?: boolean }) =>
    apiFetch<SitePageRollbackResult>(`${BASE}/${pageId}/revisions/${version}/rollback`, { method: "POST", json: input }),
  /** Мягкое удаление (право публикации, SB128). */
  /** Р3в (SB128): `redirect` — переадресация со старого адреса на выбранную цель (ручное правило). */
  remove: (pageId: string, redirect: SiteRedirectTargetInput | null = null) =>
    apiFetch<void>(`${BASE}/${pageId}`, {
      method: "DELETE",
      ...(redirect
        ? { json: { redirectTargetType: redirect.targetType, redirectTargetId: redirect.targetId, redirectTargetPath: redirect.targetPath } }
        : {}),
    }),
  /** Удалённые страницы (`?deleted=1`). */
  deleted: () => apiFetch<SitePageListItem[]>(`${BASE}?deleted=1`),
  /** Восстановить удалённую — скрытой; `slug` — новый адрес, если прежний занят. */
  restore: (pageId: string, slug?: string) =>
    apiFetch<SitePageDetail>(`${BASE}/${pageId}/restore`, { method: "POST", json: slug ? { slug } : {} }),
  /** «Сделать главной» (SB29): прежняя главная остаётся видимой, если не просили скрыть. */
  setHome: (pageId: string, input: SetHomeInput) =>
    apiFetch<SetHomeResult>(`${BASE}/${pageId}/set-home`, { method: "POST", json: input }),
};
